/** Preview of a Daily Update .docx before anything is saved — port of the original importDocx() modal. */
import { useNavigate } from 'react-router';
import type { ImportSummary, ParsedDay } from '@/types/models';
import { fmtDate, fmtH, sum } from '@/domain/format';
import { useAction } from '@/hooks/data';
import { ModalFrame } from '@/ui/kit';

const plural = (n: number) => (n === 1 ? '' : 's');

export function DocxImportModal({ fileName, days, summary, onClose }: { fileName: string; days: ParsedDay[]; summary: ImportSummary; onClose: () => void }) {
  const nav = useNavigate();
  const save = useAction(
    (s, d: ParsedDay[]) => s.backup.importDailyUpdate(d),
    (_r, d) => `Imported ${d.length} day${plural(d.length)} successfully.`,
  );
  const doImport = async () => {
    if (!(await save.run(days))) return;
    onClose();
    nav(`/daily-report?d=${days.map((d) => d.date).sort().pop()}`);
  };
  return (
    <ModalFrame
      title={`Import from ${fileName}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn primary" onClick={() => void doImport()} disabled={save.pending}>
            Import
          </button>
        </>
      }
    >
      <div className="tbl-wrap">
        <table>
          <thead>
            <tr>
              <th>Day</th>
              <th>Employee Log Work</th>
              <th>Project Log Work</th>
            </tr>
          </thead>
          <tbody>
            {days.map((d, i) => {
              const buckets = [d.general, ...Object.values(d.projects)];
              const entries = buckets.reduce((a, b) => a + b.logs.length, 0);
              const hours = buckets.reduce((a, b) => a + sum(b.logs.map((l) => l.hours)), 0);
              const projects = Object.entries(d.projects)
                .map(([n, b]) => `${n}${b.metrics.length ? ` (${b.metrics.length} stats)` : ''}`)
                .join(', ');
              return (
                <tr key={i}>
                  <td className="mono">{fmtDate(d.date)}</td>
                  <td>
                    {entries} entries · {fmtH(hours)}
                  </td>
                  <td>{projects || '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="hint" style={{ marginTop: 10 }}>
        {summary.days} day{plural(summary.days)} · {summary.workLogs} work entries · {summary.projectLogs} project logs.
        {summary.newMembers.length ? (
          <>
            {' '}
            New members will be created: <b>{summary.newMembers.join(', ')}</b>.
          </>
        ) : null}
        {summary.newProjects.length ? (
          <>
            {' '}
            New projects will be created: <b>{summary.newProjects.join(', ')}</b>.
          </>
        ) : null}
      </p>
      <p className="hint">Existing entries for the same day, member and project are replaced, so importing the same file twice is safe.</p>
    </ModalFrame>
  );
}
